// src/features/payments/pages/ContractExecutionPage.tsx
/* eslint-disable react-hooks/rules-of-hooks */

import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useProjectAccessCtx } from 'src/features/projects/ProjectAccessContext';
import { hasPerm } from 'src/features/projects/hooks/useProjectAccess';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';
import { formatProjectDateOnly, getProjectToday } from 'src/utils/date';

import api from 'src/shared/api/axios';

import PaymentMilestoneList, {
    type ActualPayment,
    type Milestone,
} from 'src/features/payments/components/execution/PaymentMilestoneList';

import { useToast } from 'src/shared/ui/toast/useToast';

type UUID = string;

type Contract = {
    id: UUID;
    projectId: UUID;
    direction: 'CLIENT' | 'SUBCON' | string;
    contractName: string;
    contractAmount: any;
    currency?: string | null;

    projectCompany?: {
        id: UUID;
        name?: string | null;
        company?: { id: UUID; name?: string | null } | null;
    } | null;

    // legacy fallback (있을 수 있음)
    partyName?: string | null;

    milestones?: Milestone[];
};

function toNum(v: any): number {
    if (v == null) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function fmtMoney(n: number, currency?: string | null) {
    const v = Math.round(toNum(n));
    return `${v.toLocaleString()}${currency ? ` ${currency}` : ''}`;
}

function companyLabel(c: Contract | null) {
    if (!c) return '(회사)';
    return c.projectCompany?.company?.name ?? c.projectCompany?.name ?? c.partyName ?? '(회사 미지정)';
}

// ✅ 최신 백엔드 mount
// api.use('/projects/:projectId/contracts/:contractId/payments', paymentsRouter)
//   POST  /milestones/:milestoneId/payments
//   PATCH /:paymentId
//   DELETE/:paymentId
const API = {
    getContract: (projectId: string, contractId: string) => `/projects/${projectId}/contracts/${contractId}`,
    createActualPayment: (projectId: string, contractId: string, milestoneId: string) =>
        `/projects/${projectId}/contracts/${contractId}/payments/milestones/${milestoneId}/payments`,
    updateActualPayment: (projectId: string, contractId: string, paymentId: string) =>
        `/projects/${projectId}/contracts/${contractId}/payments/${paymentId}`,
    deleteActualPayment: (projectId: string, contractId: string, paymentId: string) =>
        `/projects/${projectId}/contracts/${contractId}/payments/${paymentId}`,
};

type ModalState =
    | { mode: 'create'; milestoneId: string; milestoneLabel: string }
    | { mode: 'edit'; milestoneId: string; milestoneLabel: string; payment: ActualPayment };

export default function PaymentExecutionPage() {
    const toast = useToast();

    const { projectId, contractId } = useParams();
    const nav = useNavigate();
    const qc = useQueryClient();

    // 권한
    const { access, isLoading: accessLoading } = useProjectAccessCtx();
    const { setting } = useProjectSettingCtx();
    const canEditPayment = useMemo(
        () => !accessLoading && hasPerm(access?.permissions, 'payment.edit'),
        [access, accessLoading],
    );
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    const [modal, setModal] = useState<ModalState | null>(null);

    if (!projectId || !contractId) {
        return <div className="p-6 text-sm text-rose-700">잘못된 파라미터입니다.</div>;
    }

    // ✅ (1) 조회: contract + milestones + payments(include)
    const contractQ = useQuery({
        queryKey: ['paymentContractDetail', projectId, contractId],
        queryFn: async () => {
            const { data } = await api.get(API.getContract(projectId, contractId));
            const raw: Contract = (data?.data ?? data) as any;

            // minimal normalize
            const normalized: Contract = {
                ...raw,
                contractAmount: toNum((raw as any)?.contractAmount),
                milestones: Array.isArray((raw as any)?.milestones) ? (raw as any).milestones : [],
            };

            // payments 정렬(있다면)
            for (const m of normalized.milestones ?? []) {
                if (Array.isArray(m.payments)) {
                    m.payments.sort((a, b) => String(a.paidAt ?? '').localeCompare(String(b.paidAt ?? '')));
                } else {
                    m.payments = [];
                }
            }

            return normalized;
        },
        staleTime: 10_000,
    });

    const contract = contractQ.data ?? null;
    const milestones = contract?.milestones ?? [];

    // ✅ (2) ActualPayment 생성
    const createActualMut = useMutation({
        mutationFn: async (args: { milestoneId: string; amount: number; paidAt: string; note?: string | null }) => {
            const { milestoneId, amount, paidAt, note } = args;
            const payload = { amount, paidAt, note: note ?? null };
            const { data } = await api.post(API.createActualPayment(projectId, contractId, milestoneId), payload);
            return data?.data ?? data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['paymentContractDetail', projectId, contractId] });
            toast.success('지급/수령 이벤트를 저장했습니다.', {
                title: '저장 완료',
            });
        },
        onError: (err: any) => {
            console.error('[createActualPayment] failed', err);
            toast.error(err?.response?.data?.message ?? err?.message ?? 'ActualPayment 저장 실패', {
                title: '저장 실패',
            });
        },
    });

    // ✅ (3) ActualPayment 수정(PATCH)
    const updateActualMut = useMutation({
        mutationFn: async (args: { paymentId: string; amount: number; paidAt: string; note?: string | null }) => {
            const { paymentId, amount, paidAt, note } = args;
            const payload = { amount, paidAt, note: note ?? null };
            const { data } = await api.patch(API.updateActualPayment(projectId, contractId, paymentId), payload);
            return data?.data ?? data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['paymentContractDetail', projectId, contractId] });
            toast.success('지급/수령 이벤트를 수정했습니다.', {
                title: '수정 완료',
            });
        },
        onError: (err: any) => {
            console.error('[updateActualPayment] failed', err);
            toast.error(err?.response?.data?.message ?? err?.message ?? 'ActualPayment 수정 실패', {
                title: '수정 실패',
            });
        },
    });

    // ✅ (4) ActualPayment 삭제
    const deleteActualMut = useMutation({
        mutationFn: async (args: { paymentId: string }) => {
            const { paymentId } = args;
            const { data } = await api.delete(API.deleteActualPayment(projectId, contractId, paymentId));
            return data?.data ?? data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['paymentContractDetail', projectId, contractId] });
            toast.success('지급/수령 이벤트를 삭제했습니다.', {
                title: '삭제 완료',
            });
        },
        onError: (err: any) => {
            console.error('[deleteActualPayment] failed', err);
            toast.error(err?.response?.data?.message ?? err?.message ?? 'ActualPayment 삭제 실패', {
                title: '삭제 실패',
            });
        },
    });

    // ✅ (5) 파생값(표시용)
    const totals = useMemo(() => {
        const plan = milestones.reduce((sum, m) => sum + toNum(m.amountPlan), 0);
        const actual = milestones.reduce(
            (sum, m) => sum + (m.payments ?? []).reduce((s2, p) => s2 + toNum(p.amount), 0),
            0,
        );
        return { plan, actual };
    }, [milestones]);

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-lg font-semibold">Payment Execution</h1>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        className="rounded border px-3 py-1.5 text-sm bg-white hover:bg-gray-50"
                        onClick={() => nav(`/projects/${projectId}/payments`)}
                    >
                        ← Back
                    </button>
                </div>
            </div>

            {/* Contract summary */}
            <section className="rounded-2xl border bg-white p-4">
                {contractQ.isLoading ? (
                    <div className="text-sm text-gray-500">계약 로드 중…</div>
                ) : contractQ.error ? (
                    <div className="text-sm text-rose-700">
                        계약 로드 실패: {String((contractQ.error as any)?.message ?? contractQ.error)}
                    </div>
                ) : contract ? (
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <div className="text-sm font-medium">
                                {companyLabel(contract)} · {contract.contractName}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                Direction: {String(contract.direction ?? '—')}
                            </div>
                            <div className="text-xs text-gray-500">
                                Contract Amount: {fmtMoney(toNum(contract.contractAmount), contract.currency ?? null)}
                            </div>
                        </div>

                        <div className="text-right">
                            <div className="text-xs text-gray-500">Plan Total</div>
                            <div className="text-sm font-semibold">
                                {fmtMoney(totals.plan, contract.currency ?? null)}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">Actual Total</div>
                            <div className="text-sm font-semibold">
                                {fmtMoney(totals.actual, contract.currency ?? null)}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-sm text-gray-500">계약이 없습니다.</div>
                )}
            </section>

            {/* Milestones + ActualPayments */}
            {contract ? (
                <section className="rounded-2xl border bg-white p-4">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">Milestones / Actual Payments</div>
                        <div className="text-xs text-gray-500">{milestones.length} items</div>
                    </div>

                    {/* ✅ 새 리스트 컴포넌트 */}
                    <PaymentMilestoneList
                        currency={contract.currency ?? null}
                        milestones={milestones}
                        onAddPayment={(m) =>
                            setModal({
                                mode: 'create',
                                milestoneId: m.id,
                                milestoneLabel: m.label,
                            })
                        }
                        onEditPayment={(m, p) =>
                            setModal({
                                mode: 'edit',
                                milestoneId: m.id,
                                milestoneLabel: m.label,
                                payment: p,
                            })
                        }
                        onDeletePayment={(paymentId) => {
                            // eslint-disable-next-line no-restricted-globals
                            if (!confirm('이 지급/수령 이벤트를 삭제할까요?')) return;
                            deleteActualMut.mutate({ paymentId });
                        }}
                        canEdit={canEditPayment}
                    />
                </section>
            ) : null}

            {/* Create/Edit modal */}
            {modal && contract ? (
                <ActualPaymentModal
                    open
                    title={modal.milestoneLabel}
                    currency={contract.currency ?? null}
                    mode={modal.mode}
                    initial={
                        modal.mode === 'edit'
                            ? {
                                  paidAt: formatProjectDateOnly(modal.payment.paidAt, zone, ''),
                                  amount: toNum(modal.payment.amount),
                                  note: modal.payment.note ?? '',
                              }
                            : undefined
                    }
                    isSaving={createActualMut.isPending || updateActualMut.isPending}
                    onClose={() => setModal(null)}
                    onSave={(payload) => {
                        if (modal.mode === 'create') {
                            createActualMut.mutate({ milestoneId: modal.milestoneId, ...payload });
                        } else {
                            updateActualMut.mutate({ paymentId: modal.payment.id, ...payload });
                        }
                        setModal(null);
                    }}
                />
            ) : null}
        </div>
    );
}

function ActualPaymentModal(props: {
    open: boolean;
    title: string;
    currency: string | null;
    mode: 'create' | 'edit';
    initial?: { paidAt: string; amount: number; note: string };
    isSaving?: boolean;
    onClose: () => void;
    onSave: (payload: { paidAt: string; amount: number; note?: string | null }) => void;
}) {
    const { open, title, currency, mode, initial, isSaving, onClose, onSave } = props;
    if (!open) return null;

    const defaultDate = getProjectToday('Asia/Seoul');

    const [paidAt, setPaidAt] = useState<string>(initial?.paidAt ?? defaultDate);
    const [amount, setAmount] = useState<string>(initial ? String(initial.amount ?? '') : '');
    const [note, setNote] = useState<string>(initial?.note ?? '');

    const [paidAtError, setPaidAtError] = useState<string | null>(null);
    const [amountError, setAmountError] = useState<string | null>(null);

    // edit/create 전환 시 폼 초기화
    useEffect(() => {
        setPaidAt(initial?.paidAt ?? defaultDate);
        setAmount(initial ? String(initial.amount ?? '') : '');
        setNote(initial?.note ?? '');
        setPaidAtError(null);
        setAmountError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, open]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="w-[420px] rounded-2xl bg-white p-5 shadow-lg">
                <div className="mb-1 text-lg font-semibold">{mode === 'create' ? 'New Payment' : 'Edit Payment'}</div>
                <div className="mb-4 text-xs text-gray-500">{title}</div>

                <div className="space-y-3">
                    <div>
                        <div className="text-xs text-gray-500">Paid Date</div>
                        <input
                            className={`mt-1 w-full rounded border px-3 py-2 text-sm ${
                                paidAtError ? 'border-rose-400 bg-rose-50' : ''
                            }`}
                            type="date"
                            value={paidAt}
                            onChange={(e) => {
                                setPaidAt(e.target.value);
                                if (paidAtError) setPaidAtError(null);
                            }}
                        />
                        {paidAtError ? <div className="mt-1 text-xs text-rose-600">{paidAtError}</div> : null}
                    </div>

                    <div>
                        <div className="text-xs text-gray-500">Amount {currency ? `(${currency})` : ''}</div>
                        <input
                            className={`mt-1 w-full rounded border px-3 py-2 text-sm ${
                                amountError ? 'border-rose-400 bg-rose-50' : ''
                            }`}
                            type="number"
                            value={amount}
                            onChange={(e) => {
                                setAmount(e.target.value);
                                if (amountError) setAmountError(null);
                            }}
                            placeholder="0"
                        />
                        {amountError ? <div className="mt-1 text-xs text-rose-600">{amountError}</div> : null}
                    </div>

                    <div>
                        <div className="text-xs text-gray-500">Note</div>
                        <textarea
                            className="mt-1 w-full rounded border px-3 py-2 text-sm"
                            rows={3}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="선택"
                        />
                    </div>
                </div>

                <div className="mt-5 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        className="rounded border px-3 py-1.5 text-sm bg-white hover:bg-gray-50"
                        onClick={onClose}
                        disabled={!!isSaving}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="rounded px-3 py-1.5 text-sm bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-60"
                        disabled={!!isSaving}
                        onClick={() => {
                            const v = Number(amount);

                            let hasError = false;
                            setPaidAtError(null);
                            setAmountError(null);

                            if (!paidAt) {
                                setPaidAtError('지급일을 입력하세요.');
                                hasError = true;
                            }

                            if (!Number.isFinite(v) || !(v > 0)) {
                                setAmountError('금액은 0보다 커야 합니다.');
                                hasError = true;
                            }

                            if (hasError) return;

                            onSave({ paidAt, amount: v, note: note.trim() ? note.trim() : null });
                        }}
                    >
                        {isSaving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}
